/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { css, styled } from 'styled-components';

import { createResponsiveSpacingStyle, getColor } from '../../utils';
import type { Colors, ResponsiveSpacing } from '../../utils/types';

type LineProps = {
  marginTop?: ResponsiveSpacing;
  marginRight?: ResponsiveSpacing;
  marginBottom?: ResponsiveSpacing;
  marginLeft?: ResponsiveSpacing;
  color?: Colors;
  width?: ResponsiveSpacing;
  height?: number;
  variant?: 'normal' | 'dotted' | 'dashed';
  markerSize?: string;
};

const commonVariant = (height?: number, size?: string) => css`
  border-bottom: 0;
  height: ${height || 1}px;
  background-position: 0 center;
  background-size: ${size || '6px 1px'};
  background-repeat: repeat-x;
`;

const StyledNormalLine = styled.hr<LineProps>`
  margin: 0;
  border: 0;
  border-bottom: ${props => `${props.height ?? 1}px solid ${getColor(props.color ?? 'darkBlue40')}`};
  ${props => props.marginTop && `${createResponsiveSpacingStyle('margin-top', props.marginTop)}`};
  ${props => props.marginRight && `${createResponsiveSpacingStyle('margin-right', props.marginRight)}`};
  ${props => props.marginBottom && `${createResponsiveSpacingStyle('margin-bottom', props.marginBottom)}`};
  ${props => props.marginLeft && `${createResponsiveSpacingStyle('margin-left', props.marginLeft)}`};
  ${props => props.width && `${createResponsiveSpacingStyle('width', props.width)}`};

  ${props =>
    props.variant === 'dotted' &&
    css`
      ${commonVariant(props.height, props.markerSize)}
      background-image: radial-gradient(
        circle at center,
        ${getColor(props.color ?? 'white')} 0%,
        ${getColor(props.color ?? 'white')} 25%,
        transparent 25%);
    `}

  ${props =>
    props.variant === 'dashed' &&
    css`
      ${commonVariant(props.height, props.markerSize)}
      background-image: linear-gradient(to right, ${getColor(props.color ?? 'white')} 50%, transparent 0%);
    `}
`;

export const Line = ({
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  color,
  width,
  height = 1,
  variant,
  markerSize = '6px 1px',
}: LineProps) => (
  <StyledNormalLine
    marginTop={marginTop}
    marginRight={marginRight}
    marginBottom={marginBottom}
    marginLeft={marginLeft}
    color={color}
    width={width}
    height={height}
    variant={variant}
    markerSize={markerSize}
    as={variant === 'normal' ? 'hr' : 'div'}
  />
);
